// Types
import type { Dispatch, SetStateAction } from 'react';
import { useMemo, useRef, useState } from 'react';

import type { SortingState } from '@tanstack/react-table';
import { getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';

import type { SpGroupType } from '@/utils/hooks/swr/group/useSPGroups';
import type useTableColumnState from '@/utils/hooks/table/useTableColumnState';

import type { DataGroupGlobalTableAnalyticsType } from '@/utils/hooks/swr/group/useGroupGlobalTableAnalytics/types';

import type { GroupGlobalTableConfigType } from '@/lib/analyticsGroup/tables/groupGlobalTableConfig.type';

// Components
import CompoundTable from '@/components/base/Table/CompoundTable';
import type {
    GroupGlobalTableProps,
    PeriodSelectedType
} from '@/components/pages/analyze-group/tables/GroupGlobalTable';
import ActionBar from '@/components/pages/analyze-group/tables/GroupGlobalTable/ActionBar';
import TableBodyPlaceholder from '@/components/pages/analyze-group/tables/GroupGlobalTable/TableBodyPlaceholder';

type ColumnVisibilityStateType = Omit<GroupGlobalTableConfigType['columnsVisibility'], 'avatar'>;

export type GroupGlobalTableContentProps = Pick<
    GroupGlobalTableProps,
    'config' | 'userDataFormat'
> & {
    periodSelected: PeriodSelectedType;
    activeGroup: SpGroupType;
    groupAnalytics: Prettify<DataGroupGlobalTableAnalyticsType>;
    isDataLoading: boolean;
    columnVisibility: ColumnVisibilityStateType;
    columnOrder: ReturnType<typeof useTableColumnState>['columnOrder'];
    setColumnOrder: ReturnType<typeof useTableColumnState>['setColumnOrder'];
    setColumnVisibility: Dispatch<SetStateAction<ColumnVisibilityStateType>>;
};

const GroupGlobalTableContent = ({
    activeGroup,
    groupAnalytics,
    isDataLoading,
    config,
    userDataFormat,
    periodSelected,
    columnOrder,
    columnVisibility,
    setColumnOrder,
    setColumnVisibility
}: GroupGlobalTableContentProps) => {
    const columns = config.columns;

    const [sorting, setSorting] = useState<SortingState>([]);

    const emptyDataRef = useRef<DataGroupGlobalTableAnalyticsType>([]);

    const tableMeta = useMemo(
        () => ({
            ...config.meta,
            ...userDataFormat,
            periodSelected
        }),
        [config.meta, userDataFormat, periodSelected]
    );

    const tableData = groupAnalytics ?? emptyDataRef.current;

    const shouldAutoResetPageIndex = Boolean(groupAnalytics);

    const table = useReactTable({
        data: tableData,
        columns,
        meta: tableMeta,
        state: { sorting, columnVisibility, columnOrder },
        onColumnOrderChange: setColumnOrder,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        autoResetPageIndex: shouldAutoResetPageIndex
    });

    return (
        <div className="space-y-3">
            <ActionBar
                table={table}
                periodSelected={periodSelected}
                userDataFormat={userDataFormat}
                setColumnVisibility={setColumnVisibility}
            />
            <CompoundTable table={table} className="h-auto max-h-[calc(100vh-220px)]">
                <CompoundTable.Header />
                {isDataLoading ? (
                    <TableBodyPlaceholder nbRows={activeGroup?.social_profiles?.length + 1} />
                ) : (
                    <CompoundTable.Body />
                )}
            </CompoundTable>
        </div>
    );
};

export default GroupGlobalTableContent;
